import { careEnvironment } from "@akina/config";
import {
  type PropertyTypeMap,
  type SessionPropertyTypeMap,
  SessionScreenTypes,
  configureAnalyticsService,
} from "@akina/infra";

export enum CustomProperties {
  EventCause = "event_cause",
  RoutineTemplateKey = "routine_template_key",
  RoutineLogId = "routine_log_id",
  RoutineLogIndex = "routine_log_index",
  ScreenDurationTarget = "screen_duration_target",
  SelectionEnumList = "selection_enum_list",
  MediaUrl = "media_url",
  IsEducationContent = "is_education_content",
  NetworkInfo = "network_info",
  SessionBlockData = "session_block_data",
  ExerciseTemplateKey = "exercise_template_key",
  MusicMutedActive = "music_muted_active",
  MusicVolumeLevel = "music_volume_level",
  MusicVolumeChangeDirection = "music_volume_change_direction",
}

export type EventCauseType =
  | "none"
  | "submit"
  | "cancel"
  | "tab_close_exit"
  | "user_skip"
  | "user_interaction"
  | "auto_progression"
  | "10min"
  | "3min"
  | "exit";
type Section = "language" | "voice" | "sound" | "camera";

export type CustomPropertyTypeMap = {
  [CustomProperties.EventCause]?: EventCauseType;
  [CustomProperties.RoutineTemplateKey]?: string;
  [CustomProperties.RoutineLogId]?: number;
  [CustomProperties.RoutineLogIndex]?: number;
  [CustomProperties.ScreenDurationTarget]?: number;
  [CustomProperties.SelectionEnumList]?: Section[];
  [CustomProperties.MediaUrl]?: string;
  [CustomProperties.IsEducationContent]?: boolean;
  [CustomProperties.NetworkInfo]?: string;
  [CustomProperties.SessionBlockData]?: {
    fps: number;
    blockName: string;
    timestamp: string;
  };
  [CustomProperties.ExerciseTemplateKey]?: string;
  [CustomProperties.MusicMutedActive]?: boolean;
  [CustomProperties.MusicVolumeLevel]?: number;
  [CustomProperties.MusicVolumeChangeDirection]?: "none" | "increased" | "decreased";
};

enum EventType {
  SessionSetupScreenExit = "Session Setup Screen Exit",
  SessionWelcomeScreenExit = "Session Welcome Screen Exit",
  RoutineInstructionExit = "Routine Instruction Exit",
  BreakScreenExit = "Break Screen Exit",
  PauseScreenExit = "Pause Screen Exit",
  CooldownSelectionExit = "Cooldown Selection Exit",
  CooldownExit = "Cooldown Exit",
  ExerciseScreenExit = "Exercise Screen Exit",
  NetworkIssue = "Network Issue",
  //old events
  TimeSpentOnPage = "Time Spent On Page",
  PartialSessionProgress = "Partial Session Progress",
  SkippedSessionBreak = "Skipped Session Break",
  TimeSpentOnOtherTabs = "Time Spent On Other Tabs",
  CareSettingsPageExit = "Care Settings Page Exit",
}

type AnalyticsEventMap = {
  [EventType.SessionSetupScreenExit]: PropertyTypeMap & SessionPropertyTypeMap;
  [EventType.SessionWelcomeScreenExit]: PropertyTypeMap & SessionPropertyTypeMap & CustomPropertyTypeMap;
  [EventType.RoutineInstructionExit]: PropertyTypeMap & SessionPropertyTypeMap;
  [EventType.ExerciseScreenExit]: PropertyTypeMap & SessionPropertyTypeMap;
  [EventType.BreakScreenExit]: PropertyTypeMap & SessionPropertyTypeMap;
  [EventType.PauseScreenExit]: PropertyTypeMap &
    SessionPropertyTypeMap &
    Pick<
      CustomPropertyTypeMap,
      | CustomProperties.MusicMutedActive
      | CustomProperties.MusicVolumeLevel
      | CustomProperties.MusicVolumeChangeDirection
    >;
  [EventType.CooldownSelectionExit]: PropertyTypeMap & SessionPropertyTypeMap;
  [EventType.CooldownExit]: PropertyTypeMap & SessionPropertyTypeMap;
  [EventType.TimeSpentOnPage]: { pageName: string; seconds: number };
  [EventType.PartialSessionProgress]: PartialSessionProgress;
  [EventType.SkippedSessionBreak]: {
    currentTime: number;
    totalBreakTime: number;
    audioUrl: string | undefined;
  };
  [EventType.TimeSpentOnOtherTabs]: {
    seconds: number;
  };
  [EventType.CareSettingsPageExit]: PropertyTypeMap & CustomPropertyTypeMap;
  [EventType.NetworkIssue]: PropertyTypeMap & CustomPropertyTypeMap;

};

export enum PageNames {
  SessionPage = "Session Page",
}

export const mapScreenTypeToEvent = (screenType: SessionScreenTypes) => {
  switch (screenType) {
    case SessionScreenTypes.IntroVideo:
      return EventType.SessionWelcomeScreenExit;
    case SessionScreenTypes.InstructionsScreen:
      return EventType.RoutineInstructionExit;
    case SessionScreenTypes.RoutineScreen:
      return EventType.ExerciseScreenExit;
    case SessionScreenTypes.BreakScreen:
      return EventType.BreakScreenExit;
    case SessionScreenTypes.PauseScreen:
      return EventType.PauseScreenExit;
    case SessionScreenTypes.CooldownScreen:
      return EventType.CooldownExit;
    case SessionScreenTypes.CooldownSelection:
      return EventType.CooldownSelectionExit;
    default:
      return EventType.ExerciseScreenExit;
  }
};

export const analyticsService = configureAnalyticsService(!!import.meta.env.DEV, {
  amplitudeApiKey: careEnvironment.amplitudeApiKey,
  app: "akina-session",
});

type AnalyticsEventType = keyof AnalyticsEventMap;

const trackEvent = <T extends AnalyticsEventType>(eventName: T, eventData: AnalyticsEventMap[T]) => {
  analyticsService.track(eventName, eventData);
};

type PartialSessionProgress = {
  routineName: string | undefined;
  setNumber: number | undefined;
  totalNumberOfSets: number | undefined;
  screenType: string | undefined;
};

export const trackPartialSessionProgress = ({
  routineName,
  setNumber,
  totalNumberOfSets,
  screenType,
}: PartialSessionProgress) => {
  trackEvent(EventType.PartialSessionProgress, {
    routineName,
    setNumber,
    totalNumberOfSets,
    screenType,
  });
};

export const trackSessionScreenExit = (
  eventName: EventType,
  analyticsData: PropertyTypeMap & SessionPropertyTypeMap & CustomPropertyTypeMap,
) => {
  trackEvent(eventName, analyticsData);
};
